<script setup lang="ts">
// Static illustration for the "Analytics" landing section, mirroring the
// real statistics dashboard: stat tiles, the views/clicks chart, countries.
const countries = [
  { code: 'NL', name: 'Netherlands', count: 312, pct: 100 },
  { code: 'US', name: 'United States', count: 198, pct: 63 },
  { code: 'DE', name: 'Germany', count: 141, pct: 45 }
]
</script>

<template>
  <div
    aria-hidden="true"
    class="w-full select-none pointer-events-none rounded-2xl border border-default bg-elevated/30 p-4 sm:p-5 shadow-(--surface-shadow)"
  >
    <div class="rounded-2xl border border-(--mock-card-border) bg-(--mock-card-solid) p-3.5">
      <!-- Stat tiles -->
      <div class="grid grid-cols-3 gap-2.5">
        <div class="rounded-xl border border-(--mock-card-border) bg-(--mock-tile-bg) p-3">
          <span class="flex size-7 items-center justify-center rounded-lg bg-emerald-500/15">
            <UIcon
              name="i-lucide-eye"
              class="size-3.5 text-emerald-400"
            />
          </span>
          <p class="mt-2 text-xl font-bold tabular-nums text-(--mock-text-strong)">
            1,284
          </p>
          <p class="text-[9px] font-medium uppercase tracking-wider text-(--mock-text-dim)">
            Views
          </p>
        </div>
        <div class="rounded-xl border border-(--mock-card-border) bg-(--mock-tile-bg) p-3">
          <span class="flex size-7 items-center justify-center rounded-lg bg-sky-500/15">
            <UIcon
              name="i-lucide-mouse-pointer-click"
              class="size-3.5 text-sky-400"
            />
          </span>
          <p class="mt-2 text-xl font-bold tabular-nums text-(--mock-text-strong)">
            386
          </p>
          <p class="text-[9px] font-medium uppercase tracking-wider text-(--mock-text-dim)">
            Clicks
          </p>
        </div>
        <div class="rounded-xl border border-(--mock-card-border) bg-(--mock-tile-bg) p-3">
          <span class="relative flex size-7 items-center justify-center rounded-lg bg-green-500/15">
            <span class="live-dot size-2 rounded-full bg-green-400" />
          </span>
          <p class="mt-2 text-xl font-bold tabular-nums text-(--mock-text-strong)">
            3
          </p>
          <p class="text-[9px] font-medium uppercase tracking-wider text-(--mock-text-dim)">
            Live visitors
          </p>
        </div>
      </div>

      <!-- Views & clicks over time -->
      <div class="mt-2.5 rounded-xl border border-(--mock-card-border) bg-(--mock-tile-bg) p-3.5">
        <div class="flex items-center justify-between">
          <p class="text-[10px] font-medium text-(--mock-text-muted)">
            Views & clicks over time
          </p>
          <div class="flex items-center gap-3">
            <span class="flex items-center gap-1 text-[9px] text-(--mock-text-dim)">
              <span class="size-1.5 rounded-full bg-primary-400" />
              Views
            </span>
            <span class="flex items-center gap-1 text-[9px] text-(--mock-text-dim)">
              <span class="size-1.5 rounded-full bg-emerald-400" />
              Clicks
            </span>
          </div>
        </div>
        <svg
          viewBox="0 0 320 80"
          class="mt-2 h-20 w-full"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient
              id="mock-views-fill"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stop-color="#8d8df8"
                stop-opacity="0.25"
              />
              <stop
                offset="100%"
                stop-color="#8d8df8"
                stop-opacity="0"
              />
            </linearGradient>
            <linearGradient
              id="mock-clicks-fill"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stop-color="#34d399"
                stop-opacity="0.2"
              />
              <stop
                offset="100%"
                stop-color="#34d399"
                stop-opacity="0"
              />
            </linearGradient>
          </defs>
          <path
            d="M0 66 C 30 62, 50 44, 80 40 C 110 36, 130 18, 160 16 C 190 14, 210 30, 240 34 C 270 38, 295 26, 320 22 L 320 80 L 0 80 Z"
            fill="url(#mock-views-fill)"
          />
          <path
            d="M0 66 C 30 62, 50 44, 80 40 C 110 36, 130 18, 160 16 C 190 14, 210 30, 240 34 C 270 38, 295 26, 320 22"
            fill="none"
            stroke="#8d8df8"
            stroke-width="1.5"
          />
          <path
            d="M0 74 C 30 72, 55 62, 85 60 C 115 58, 140 46, 170 46 C 200 46, 225 54, 250 52 C 275 50, 300 44, 320 42 L 320 80 L 0 80 Z"
            fill="url(#mock-clicks-fill)"
          />
          <path
            d="M0 74 C 30 72, 55 62, 85 60 C 115 58, 140 46, 170 46 C 200 46, 225 54, 250 52 C 275 50, 300 44, 320 42"
            fill="none"
            stroke="#34d399"
            stroke-width="1.5"
          />
        </svg>
      </div>

      <!-- Top countries -->
      <div class="mt-2.5 rounded-xl border border-(--mock-card-border) bg-(--mock-tile-bg) p-3.5">
        <p class="text-[10px] font-medium text-(--mock-text-muted)">
          Top countries
        </p>
        <div class="mt-2.5 flex flex-col gap-2">
          <div
            v-for="country in countries"
            :key="country.code"
            class="flex items-center gap-2.5"
          >
            <span class="w-6 shrink-0 text-[10px] font-semibold text-(--mock-text-muted)">{{ country.code }}</span>
            <span class="w-20 shrink-0 truncate text-[10px] text-(--mock-text)">{{ country.name }}</span>
            <div class="h-1.5 flex-1 rounded-full bg-(--mock-track)">
              <div
                class="h-full rounded-full bg-primary-400/70"
                :style="{ width: `${country.pct}%` }"
              />
            </div>
            <span class="w-8 shrink-0 text-right text-[10px] tabular-nums text-(--mock-text-dim)">{{ country.count }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.live-dot {
  animation: live-pulse 1.6s ease-in-out infinite;
}

@keyframes live-pulse {
  0%, 100% {
    opacity: 1;
  }

  50% {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .live-dot {
    animation: none;
  }
}
</style>
